import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { FinecoDownloadButton } from "pages/assistant/components/FinecoDownloadButton";
import { AssistantService } from "shared/services/AssistantService";
import { useToast } from "shared/components/ui/use-toast";
import { GenerateComparisonReport } from "./GenerateComparisonReport";
import { getComparisonPdfFilename } from "utils/comparisonUtils";
import { FinecoDownloadIcon } from "shared/assets/svg/FinecoDownloadIcon";

import styles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";
import linkStyles from "pages/assistant/components/finecoDownloadButton.module.scss";
import { createLogger } from "shared/logger";

const log = createLogger("PortfolioCompareButtons");

type FinecoPortfolioCompareButtonsProps = {
  threadId: string;
  portfolioId1: string;
  portfolioId2: string;
  countervalueTotal1?: number;
  countervalueTotal2?: number;
  commentoAlPortafoglio1?: boolean;
  commentoAlPortafoglio2?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
};

export const FinecoPortfolioCompareButtons: React.FC<FinecoPortfolioCompareButtonsProps> = ({
  threadId,
  portfolioId1,
  portfolioId2,
  countervalueTotal1,
  countervalueTotal2,
  commentoAlPortafoglio1,
  commentoAlPortafoglio2,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
  const [generatingDownloadPdf, setGeneratingDownloadPdf] = useState<boolean>(false);
  const generateReportRef = useRef(null);

  const requestGenerateComparisonPdf = useCallback(
    async (reportSpec: any, fromReport: boolean = false) => {
      if (fromReport) {
        setGeneratingPdf(true);
      }

      try {
        let rawPdf;

        if (portfolioId1 && portfolioId2) {
          // Build query params for backtest dates
          const queryParams: { backtest_start?: string; backtest_end?: string } = {};
          if (startDate) {
            queryParams.backtest_start = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
          }
          if (endDate) {
            queryParams.backtest_end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
          }

          const { portfolio_labels, portfolio_short_labels, ...reportParams } = reportSpec;

          rawPdf = await AssistantService.createComparisonPdf(
            portfolioId1,
            portfolioId2,
            reportParams,
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
            portfolio_labels,
            portfolio_short_labels,
          );
        } else {
          throw new Error("No portfolio IDs available for comparison PDF generation");
        }

        const blob = new Blob([rawPdf], { type: "application/pdf" });
        if (blob.type === "application/pdf") {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = getComparisonPdfFilename(portfolioId1, portfolioId2);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          toast({
            description: t("portafoglioCompare.downloadedSuccess"),
            title: t("global.success"),
          });
        } else {
          toast({
            description: t("portafoglioCompare.downloadError"),
            title: t("global.error"),
            variant: "destructive",
          });
        }
      } catch (error) {
        log.error("portfolio comparison PDF generation failed", { error });
        let errorMessage = t("portafoglioCompare.downloadError");

        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        toast({
          description: errorMessage,
          title: t("global.error"),
          variant: "destructive",
        });
      } finally {
        setGeneratingPdf(false);
        setGeneratingDownloadPdf(false);
      }
    },
    [threadId, portfolioId1, portfolioId2, startDate, endDate, t, toast],
  );

  const isGenerating = generatingPdf || generatingDownloadPdf;

  return (
    <div className={styles.buttonContainer}>
      <FinecoDownloadButton
        click={() => {
          setGeneratingDownloadPdf(true);
          generateReportRef.current.generate(false);
        }}
        copy={
          !generatingDownloadPdf
            ? t("generateReport.download")
            : t("generateReport.downloading")
        }
        disabled={isGenerating}
      />
      <GenerateComparisonReport
        threadId={threadId}
        portfolioId1={portfolioId1}
        portfolioId2={portfolioId2}
        countervalueTotal1={countervalueTotal1}
        countervalueTotal2={countervalueTotal2}
        commentoAlPortafoglio1={commentoAlPortafoglio1}
        commentoAlPortafoglio2={commentoAlPortafoglio2}
        onGenerate={requestGenerateComparisonPdf}
        ref={generateReportRef}
        disabled={isGenerating}
      >
        <div
          className={linkStyles.buttonContainer}
          style={isGenerating ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <div className={linkStyles.icon}>
            <FinecoDownloadIcon />
          </div>
          <div className={linkStyles.buttonCopy}>
            {!generatingPdf
              ? t("generateReport.modify")
              : t("generateReport.downloading")}
          </div>
        </div>
      </GenerateComparisonReport>
    </div>
  );
};